import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Periwinkle corner light. Use on at most one panel per page. */
  featured?: boolean;
}

/** The S-FX card: 14px radius, one hairline, faint top sheen, no shadow. */
export function Card({ featured, className, children, ...props }: CardProps) {
  return (
    <div className={cn('sfx-card overflow-hidden', featured && 'sfx-sheen', className)} {...props}>
      {children}
    </div>
  );
}

interface CardHeaderProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: LucideIcon;
  /** Right-aligned slot: a link, an icon button, a date. */
  action?: React.ReactNode;
  className?: string;
}

export function CardHeader({ title, description, icon: Icon, action, className }: CardHeaderProps) {
  return (
    <div className={cn('flex items-start justify-between gap-4 px-5 pt-5 pb-4', className)}>
      <div className="flex min-w-0 items-start gap-3">
        {Icon && (
          <span className="sfx-icon-tile h-8 w-8">
            <Icon className="h-4 w-4" strokeWidth={2} />
          </span>
        )}
        <div className="min-w-0">
          <h3 className="font-display text-[15px] font-semibold leading-tight text-foreground">
            {title}
          </h3>
          {description && (
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground text-pretty">
              {description}
            </p>
          )}
        </div>
      </div>
      {action && <div className="flex shrink-0 items-center gap-1">{action}</div>}
    </div>
  );
}

/** Text link styled as the accent "see more" affordance used in card headers. */
export function CardLink({
  children,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={cn(
        'group inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium text-accent',
        'transition-colors duration-fast ease-sfx hover:bg-accent/10',
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}
